import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '@phase2/outline-core';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import componentStyles from './pc-accordion-panel.css.lit';
import { PCAccordionContent } from '../pc-accordion-content/pc-accordion-content';
import '../../pc-icon/pc-icon';
import { PCAccordionTrigger } from '../pc-accordion-trigger/pc-accordion-trigger';

/**
 * The Accordion Panel component
 * @element pc-accordion-panel
 */
@customElement('pc-accordion-panel')
export class PCAccordionPanel extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'variant', reflect: true })
  variant: 'primary' | 'secondary' = 'primary';

  @property({ type: String, attribute: 'aria-label' })
  ariaLabel: string;

  @queryAssignedElements({})
  _childItems!: Array<HTMLElement>;

  trigger: PCAccordionTrigger;
  content: PCAccordionContent;

  /**
   * Wether the panel is active/open.
   * Controlled by parent accordion component.
   */
  @property({ type: Boolean, reflect: true })
  active: boolean;

  @property({ type: Boolean, reflect: true, attribute: 'default-active' })
  defaultActive: boolean;

  /**
   * Used to provided a unique ID
   * for accordion component and accessibility purposes.
   */
  @property({ type: String, reflect: true })
  id: string = Math.floor(Math.random() * 10000).toString();

  protected firstUpdated(): void {
    if (
      this._childItems[0] instanceof PCAccordionTrigger &&
      this._childItems[1] instanceof PCAccordionContent
    ) {
      this.trigger = this._childItems[0];
      this.content = this._childItems[1];

      this.trigger.setAttribute('id', `${this.id}-trigger`);
      this.trigger.setAttribute('aria-controls', `${this.id}-content`);
      this.trigger.setAttribute('aria-label', `expand ${this.ariaLabel}`);
      this.trigger.variant = this.variant;

      this.content.setAttribute('id', `${this.id}-content`);
      this.content.setAttribute('aria-labelledby', `${this.id}-trigger`);
      this.setAttribute('role', 'region');

      this.trigger.addEventListener('click', () => {
        this.setActive();
      });

      if (this.defaultActive) {
        this.content.defaultActive = true;
        this.trigger.active = true;
      }
    }
  }

  render(): TemplateResult {
    return html`
      <section class="accordion-panel">
        <slot></slot>
      </section>
    `;
  }

  setActive() {
    const isOpen = this.active ?? this.defaultActive;

    this.active = !isOpen;
    this.content.active = !isOpen;
    this.trigger.active = !isOpen;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-accordion-panel': PCAccordionPanel;
  }
}
